import { Colors } from '@/constants/theme';
import React, { ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { View } from './tw-wrappers';

export function HoverCard({
  children,
  backgroundColor,
  borderColor,
  borderWidth,
  className,
}: {
  children: ReactNode;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  className?: string;
}) {
  const hoverProgress = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(() => {
    const offset = interpolate(hoverProgress.value, [0, 1], [4, 0]);
    const radius = interpolate(hoverProgress.value, [0, 1], [0, 10]);
    const opacity = interpolate(hoverProgress.value, [0, 1], [1, 0.8]);
    const shadowColor = interpolateColor(
      hoverProgress.value,
      [0, 1],
      [Colors.light.shadow, Colors.light.primary]
    );

    // Calculate RGBA for web boxShadow
    // Gray: rgb(170, 170, 170), Primary: rgb(11, 95, 255)
    const r = Math.round(interpolate(hoverProgress.value, [0, 1], [170, 11]));
    const g = Math.round(interpolate(hoverProgress.value, [0, 1], [170, 95]));
    const b = Math.round(interpolate(hoverProgress.value, [0, 1], [170, 255]));

    return {
      shadowColor,
      shadowOffset: {
        width: offset,
        height: offset,
      },
      shadowRadius: radius,
      shadowOpacity: opacity,
      elevation: interpolate(hoverProgress.value, [0, 1], [2, 12]),
      // @ts-ignore - boxShadow is web-only
      boxShadow: `${offset}px ${offset}px ${radius}px rgba(${r}, ${g}, ${b}, ${opacity})`,
    };
  });

  return (
    <View
      className={className}
      onMouseEnter={() => (hoverProgress.value = withTiming(1, { duration: 400 }))}
      onMouseLeave={() => (hoverProgress.value = withTiming(0, { duration: 400 }))}
    >
      <Animated.View
        style={[
          styles.card,
          backgroundColor ? { backgroundColor } : { backgroundColor: '#ffffff' },
          borderColor ? { borderColor } : null,
          borderWidth ? { borderWidth } : null,
          animatedStyle,
        ]}
      >
        {children}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
    shadowColor: Colors.light.shadow,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
    gap: 12,
    position: 'relative',
  },
});
